import FacebookSocialIcon from "@/components/_icons/facebookSocialIcon";
import InstagramSocialIcon from "@/components/_icons/instagramSocialIcon";
import LocalizedPhoneDisplay from "../_localized/localizedPhoneDisplay";
import React from "react";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";
import { useNativeActions } from "@/utils/useNativeActions";

export const BusinessLocations = ({
  merchantName,
  merchantAddress,
  merchantPhoneNumber,
  merchantLocale,
  merchantEmail,
  merchantColor,
  facebookUrl,
  instagramUrl,
}: any) => {
  const t = useTranslations("BusinessLocations");
  const { isCmaApp, openExternalLink } = useNativeActions();

  const handleExternalLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string
  ) => {
    if (isCmaApp) {
      e.preventDefault();
      openExternalLink(url);
    }
  };

  const fullAddress = `${merchantAddress.address1} ${
    merchantAddress.address2 ? merchantAddress.address2 + " " : ""
  }${merchantAddress.city} ${merchantAddress.state} ${merchantAddress.zip}`;
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(
    fullAddress
  )}`;
  const phoneUrl = `tel:${merchantPhoneNumber}`;

  const addressContent = (
    <>
      <p className={["text-[15px] leading-[21.75px] font-normal"].join(" ")}>
        {merchantAddress.address1 + " "}
      </p>
      {merchantAddress.address2 && (
        <p className={["text-[15px] leading-[21.75px] font-normal"].join(" ")}>
          {merchantAddress.address2 + ", "}
        </p>
      )}
      <div
        className={[
          "flex",
          "flex-col",
          "max-md:flex-row",
          "max-md:flex-wrap",
          "gap-y-2",
          "gap-x-1",
          "text-[15px]",
          "leading-[21.75px]",
          "font-normal",
        ].join(" ")}
      >
        <span>{merchantAddress.city}</span>
        <span>{merchantAddress.state}</span>
        <span>{merchantAddress.zip}</span>
      </div>
    </>
  );

  const phoneContent = (
    <LocalizedPhoneDisplay
      phoneNumber={merchantPhoneNumber}
      locale={merchantLocale}
      international={true}
    />
  );

  return (
    <div
      data-testid={applyDataTestId(pom.merchantFooter.businessLocationsSection)}
      className={[
        "flex",
        "flex-col",
        "w-[240px]",
        "max-md:ml-0",
        "max-md:w-full",
        "max-md:px-5",
        "mr-2",
        "gap-5",
        "flex justify-start items-start pb-4",
      ].join(" ")}
    >
      <span className={["text-[20px] leading-[25px] font-semibold"].join(" ")}>
        {merchantName}
      </span>
      <div
        data-testid={applyDataTestId(pom.footer.contactInfo)}
        className={[
          "flex",
          "flex-col",
          "gap-2",
          "flex justify-start items-start",
        ].join(" ")}
      >
        {isCmaApp ? (
          <button
            onClick={() => openExternalLink(mapsUrl)}
            className="text-left hover:opacity-80 transition-opacity"
          >
            {addressContent}
          </button>
        ) : (
          <div>{addressContent}</div>
        )}
        {isCmaApp ? (
          <button
            onClick={() => openExternalLink(phoneUrl)}
            className="text-[15px] leading-[21.75px] font-normal text-left hover:underline"
          >
            {phoneContent}
          </button>
        ) : (
          <p
            className={["text-[15px] leading-[21.75px] font-normal"].join(" ")}
          >
            {phoneContent}
          </p>
        )}
        <a
          href={`mailto:${merchantEmail}`}
          onClick={(e) => handleExternalLinkClick(e, `mailto:${merchantEmail}`)}
          className={`max-w-[240px] break-words underline text-[15px] leading-[21.75px] font-normal`}
          style={{ color: merchantColor }}
          data-testid={applyDataTestId(pom.merchantFooter.customerContactEmail)}
        >
          {merchantEmail}
        </a>
      </div>
      <div
        data-testid={applyDataTestId(pom.merchantFooter.socialMediaLinks)}
        className={[
          "flex",
          "flex-row",
          "flex justify-between self-start gap-[30px]",
        ].join(" ")}
      >
        {facebookUrl && (
          <a
            href={facebookUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => handleExternalLinkClick(e, facebookUrl)}
          >
            <FacebookSocialIcon
              data-testid={applyDataTestId(pom.merchantFooter.facebookIcon)}
            />
          </a>
        )}
        {instagramUrl && (
          <a
            href={instagramUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => handleExternalLinkClick(e, instagramUrl)}
          >
            <InstagramSocialIcon
              data-testid={applyDataTestId(pom.merchantFooter.instagramIcon)}
            />
          </a>
        )}
      </div>
    </div>
  );
};
